import React, { useEffect, useRef, useState } from "react";
import { pickPreviewSrc } from "../lib/templates";
import { renderOverlay } from "../lib/render";
import { sanitize } from "../lib/sanitize";

function BgLayer({ background, onVideo, onDuration }) {
  const ref = useRef(null);
  const hlsRef = useRef(null);
  useEffect(() => {
    const mount = ref.current;
    if (!mount) return;
    if (hlsRef.current) { try { hlsRef.current.destroy(); } catch (e) { console.debug("hls destroy", e); } hlsRef.current = null; }
    mount.replaceChildren(); // clear safely — no innerHTML
    const bg = background || {};
    const pv = pickPreviewSrc(bg);
    const scan = document.createElement("div"); scan.className = "scan";
    const placeholder = (text) => { const d = document.createElement("div"); d.className = "ph"; d.textContent = text; return d; };

    if (bg.type === "video" && pv) {
      const v = document.createElement("video");
      v.muted = true; v.loop = true; v.setAttribute("playsinline", "");
      v.addEventListener("loadedmetadata", () => { if (onDuration && isFinite(v.duration) && v.duration > 0) onDuration(v.duration); });
      const url = pv.url;
      if (pv.kind === "hls" || /\.m3u8(\?|$)/i.test(url)) {
        if (window.Hls && window.Hls.isSupported()) {
          const h = new window.Hls(); h.loadSource(url); h.attachMedia(v); hlsRef.current = h;
        } else { v.src = url; }
      } else { v.src = url; }
      mount.appendChild(v); mount.appendChild(scan);
      v.play().catch((e) => { console.debug("autoplay blocked (expected)", e); });
      if (onVideo) onVideo(v);
    } else if (bg.type === "image" && bg.image) {
      const img = document.createElement("img");
      img.src = bg.image; img.alt = ""; // .src is URL-safe, no HTML injection
      mount.appendChild(img); mount.appendChild(scan);
      if (onVideo) onVideo(null);
    } else if (bg.type === "partner") {
      mount.appendChild(placeholder("partner VAST video — plays in Present & in the exported tag"));
      mount.appendChild(scan);
      if (onVideo) onVideo(null);
    } else {
      mount.appendChild(placeholder(bg.type === "video" ? "add a video rendition →" : "add a background image →"));
      mount.appendChild(scan);
      if (onVideo) onVideo(null);
    }
    return () => { if (hlsRef.current) { try { hlsRef.current.destroy(); } catch (e) { console.debug("hls destroy", e); } hlsRef.current = null; } };
  }, [JSON.stringify(background)]); // eslint-disable-line react-hooks/exhaustive-deps
  return <div className="bgLayer" ref={ref} />;
}

export default function Preview({ cfg, tid, i, voted, onKeyDown, tvRef, onDuration }) {
  const html = renderOverlay(tid, cfg, i, voted);
  const videoRef = useRef(null);
  const duration = Math.max(1, parseInt(cfg.duration, 10) || 15);
  const [paused, setPaused] = useState(false);
  const [ended, setEnded] = useState(false);
  const [remaining, setRemaining] = useState(duration);

  // reset when duration or background changes
  useEffect(() => { setRemaining(duration); setEnded(false); setPaused(false); }, [duration, JSON.stringify(cfg.background)]); // eslint-disable-line

  // countdown tick
  useEffect(() => {
    if (paused || ended) return;
    if (remaining <= 0) { setEnded(true); const v = videoRef.current; if (v) v.pause(); return; }
    const t = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(t);
  }, [remaining, paused, ended]);

  // keep the video element in sync with play/pause/ended
  useEffect(() => {
    const v = videoRef.current; if (!v) return;
    if (paused || ended) v.pause(); else v.play().catch(() => {});
  }, [paused, ended]);

  const setVideo = (v) => { videoRef.current = v; if (v && (paused || ended)) v.pause(); };
  const replay = () => {
    setEnded(false); setPaused(false); setRemaining(duration);
    const v = videoRef.current; if (v) { try { v.currentTime = 0; v.play().catch(() => {}); } catch (e) { console.debug("replay seek", e); } }
  };
  const togglePlay = () => {
    if (ended) return replay();
    setPaused((p) => !p);
  };
  const mm = Math.floor(remaining / 60);
  const ss = String(Math.max(0, remaining) % 60).padStart(2, "0");

  return (
    <>
      <div className="tv" tabIndex={0} ref={tvRef} role="application"
           aria-label="Ad preview — arrow keys and Enter" data-testid="tv-preview"
           onKeyDown={onKeyDown} onClick={() => tvRef && tvRef.current && tvRef.current.focus()}>
        <BgLayer background={cfg.background} onVideo={setVideo} onDuration={onDuration} />
        <div className="overlay" dangerouslySetInnerHTML={{ __html: sanitize(html) }} />
        <div className="countdown" data-testid="preview-countdown">{mm}:{ss}</div>
        {ended && (
          <div className="ad-ended" data-testid="ad-ended">
            <div>Ad ended</div>
            <button className="btn primary" onClick={replay} data-testid="preview-replay">↻ Replay</button>
          </div>
        )}
      </div>
      <div className="playbar" data-testid="playbar">
        <button className="btn" onClick={togglePlay} data-testid="play-toggle">
          {ended ? "↻ Replay" : paused ? "▶ Play" : "❙❙ Pause"}
        </button>
        <button className="btn" onClick={replay} data-testid="restart-btn">↞ Restart</button>
        <span className="hint">{duration}s ad · {ended ? "ended" : paused ? "paused" : "playing"}</span>
      </div>
    </>
  );
}
